import { api } from "./client";

export type FeedbackRating = "bad" | "okay" | "good" | "amazing";
export type FeedbackStatus = "unread" | "reviewed" | "archived";

export interface FeedbackItem {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  user_role: string;
  user_organization: string;
  rating: FeedbackRating;
  suggestions: string;
  status: FeedbackStatus;
  created_at: string;
  updated_at: string;
}

export interface FeedbackSummary {
  unread_count: number;
  total_count: number;
  recent: FeedbackItem[];
}

export interface FeedbackListResponse {
  total: number;
  limit: number;
  skip: number;
  items: FeedbackItem[];
}

export interface CreateFeedbackPayload {
  rating: FeedbackRating;
  suggestions: string;
}

/**
 * Submit new product feedback
 */
export async function submitFeedback(payload: CreateFeedbackPayload): Promise<{
  status: string;
  message: string;
  data: FeedbackItem;
}> {
  const response = await api.post("/feedback", payload);
  return response.data;
}

/**
 * Get feedback summary & unread count (Admin/Analyst only)
 */
export async function getFeedbackSummary(): Promise<FeedbackSummary> {
  const response = await api.get<FeedbackSummary>("/feedback/summary");
  return response.data;
}

/**
 * List paginated feedback (Admin/Analyst only)
 */
export async function listFeedback(params?: {
  status?: FeedbackStatus;
  limit?: number;
  skip?: number;
}): Promise<FeedbackListResponse> {
  const response = await api.get<FeedbackListResponse>("/feedback", {
    params,
  });
  return response.data;
}

/**
 * Update single feedback status
 */
export async function updateFeedbackStatus(
  feedbackId: string,
  status: FeedbackStatus
): Promise<{ status: string; message: string; data: FeedbackItem }> {
  const response = await api.patch(`/feedback/${feedbackId}/status`, { status });
  return response.data;
}

/**
 * Mark all unread feedbacks as reviewed
 */
export async function markAllFeedbackRead(): Promise<{
  status: string;
  modified_count: number;
}> {
  const response = await api.post("/feedback/mark-all-read");
  return response.data;
}
